<template>
  <div>
    <v-row>
      <v-col>
        <s-crud
          title="Gestión de Alertas"
          :config="config"
          edit
          add
          :rowDefault="false"
          remove
          search-input
          ref="crud"
          height="200"
          @save="save($event)"
          @rowSelected="rowSelected($event)"
        >
          <template slot-scope="props">
            <v-row class="mt-0 mb-0 ml-5 mr-5">
              <v-col cols="12" sm="5">
                <s-text label="Nombre de la Alerta" v-model="props.item.AldName" />
              </v-col>
              <v-col cols="12" sm="3">
                <s-select-definition
                  :def="1148"
                  label="Condición"
                  v-model="props.item.TypeCondition"
                />
              </v-col>
              <v-col cols="12" sm="4">
                <s-select-lot
                  label="Lote"
                  clearable
                  v-model="props.item.FltID"
                  full
                  noDefault
                />
              </v-col>
              <v-col cols="12" sm="4" v-if="props.item.TypeCondition !== 0">
                <s-text label="Valor" v-model="props.item.AldValue" />
              </v-col>

              <!-- Modo simple (no fórmula): tabla y campo -->
              <template v-if="props.item.TypeCondition !== 0">
                <v-col cols="12" sm="4">
                  <s-select
                    :items="tablesItems"
                    item-value="value"
                    item-text="text"
                    label="Tabla Relacionada"
                    v-model="props.item.AldRelatedTable"
                    @change="onChangeTable($event, props.item)"
                    autocomplete
                    clearable
                  />
                </v-col>
                <v-col cols="12" sm="4">
                  <s-select
                    :items="columnsItems"
                    item-value="value"
                    item-text="text"
                    label="Campo Relacionado"
                    v-model="props.item.AldRelatedField"
                    autocomplete
                    clearable
                  />
                </v-col>
              </template>

              <!-- Builder de fórmula -->
              <template v-else>
                <v-col cols="12">
                  <div class="formula-container">
                    <!-- Panel izquierdo: Selección de tablas -->
                    <div class="tables-panel">
                      <div class="panel-header">
                        <h3 class="panel-title">Fuentes de Datos</h3>
                      </div>

                      <div class="table-selector">
                        <s-select
                          :items="tablesItems"
                          item-value="value"
                          item-text="text"
                          label="Agregar tabla"
                          clearable
                          autocomplete
                          :return-object="true"
                          @change="addTable($event)"
                        />
                      </div>

                      <div class="tables-list">
                        <div v-if="selectedTables.length === 0" class="empty-state">
                          <v-icon color="grey lighten-1" size="48">mdi-table-off</v-icon>
                          <p>Selecciona una tabla para comenzar</p>
                        </div>

                        <div
                          v-for="t in selectedTables"
                          :key="'tbl-' + t.name"
                          class="table-item"
                        >
                          <div class="table-header">
                            <span class="table-name">{{ t.text }}</span>
                            <v-btn
                              x-small
                              icon
                              @click="removeTable(t)"
                              class="remove-btn"
                            >
                              <v-icon size="16">mdi-close</v-icon>
                            </v-btn>
                          </div>
                          <div class="columns-grid">
                            <v-chip
                              v-for="c in columnsByTable[t.name] || []"
                              :key="t.name + '-' + (c && c.value ? c.value : c)"
                              :class="getColumnChipClass(t, c)"
                              :title="getColumnChipTooltip(t, c)"
                              outlined
                              small
                              @click="insertTokenFrom(t, c, props.item)"
                            >
                              <v-icon left size="14">mdi-plus-circle-outline</v-icon>
                              {{ c && c.text ? c.text : c }}
                            </v-chip>
                          </div>
                        </div>
                      </div>
                    </div>

                    <!-- Panel central: Editor de fórmula -->
                    <div class="formula-panel">
                      <div class="panel-header">
                        <h3 class="panel-title">Constructor de Fórmula</h3>
                      </div>

                      <!-- Toolbar de operadores -->
                      <div class="formula-toolbar">
                        <div class="toolbar-section">
                          <label class="toolbar-label">Operadores</label>
                          <div class="button-grid">
                            <v-btn
                              v-for="op in operators"
                              :key="op.symbol"
                              small
                              outlined
                              class="operator-btn"
                              @click="insertOperator(op.value, props.item)"
                            >
                              {{ op.symbol }}
                            </v-btn>
                          </div>
                        </div>
                        <!-- Toolbar de operadores lógicos -->
                        <div class="toolbar-section">
                          <label class="toolbar-label">Operadores Lógicos</label>
                          <div class="button-grid">
                            <v-btn
                              v-for="lop in logicalOperators"
                              :key="lop.symbol"
                              small
                              outlined
                              class="operator-btn"
                              @click="insertOperator(lop.value, props.item)"
                            >
                              {{ lop.symbol }}
                            </v-btn>
                          </div>
                        </div>
                        <!-- Toolbar de funciones -->
                        <div class="toolbar-section">
                          <label class="toolbar-label">Funciones</label>
                          <div class="button-grid">
                            <v-btn
                              v-for="f in functions"
                              :key="f.label"
                              small
                              outlined
                              color="primary"
                              class="function-btn"
                              @click="insertFormulaSnippet(f.template, props.item)"
                            >
                              <v-icon left size="14">mdi-function-variant</v-icon>
                              {{ f.label }}
                            </v-btn>
                          </div>
                        </div>
                      </div>

                      <!-- Editor de tokens -->
                      <div class="editor-wrapper">
                        <token-editor
                          :ref="`tokenEditor_${props.item.AldID}`"
                          :value="props.item.AldFormula"
                          @input="updateFormula($event, props.item)"
                          :tokens="badgeTokens"
                          :token-classes="builderTokenClasses"
                          :token-tooltips="builderTokenTooltips"
                          :no-typing="true"
                        />
                        <!-- Probe visual oculto para forzar evaluación de builderTokens -->
                        <div
                          aria-hidden="true"
                          style="
                            position: absolute;
                            left: -9999px;
                            top: -9999px;
                            opacity: 0;
                            height: 0;
                            width: 0;
                            overflow: hidden;
                          "
                        >
                          {{ builderTokens && builderTokens.length }}
                        </div>
                        <div class="editor-hint">
                          <v-icon small color="info" class="mr-1"
                            >mdi-information-outline</v-icon
                          >
                          <span>
                            Construye tu fórmula usando los botones. Las variables
                            aparecen como chips azules. Solo puedes escribir números y
                            texto entre comillas.
                          </span>
                        </div>
                      </div>

                      <!-- Botones de acción -->
                      <div class="action-buttons">
                        <v-btn
                          color="error"
                          outlined
                          small
                          @click="clearFormula(props.item)"
                        >
                          <v-icon left size="18">mdi-delete-outline</v-icon>
                          Limpiar
                        </v-btn>
                        <v-spacer></v-spacer>
                        <v-btn color="primary" small @click="validate(props.item)">
                          <v-icon left size="18">mdi-check-circle-outline</v-icon>
                          Validar
                        </v-btn>
                      </div>
                    </div>

                    <!-- Panel derecho: Pruebas -->
                    <div class="test-panel">
                      <div class="panel-header">
                        <h3 class="panel-title">Probar Fórmula</h3>
                      </div>

                      <div class="test-content">
                        <div v-if="builderTokens.length === 0" class="empty-state">
                          <v-icon color="grey lighten-1" size="48"
                            >mdi-calculator-variant-outline</v-icon
                          >
                          <p>Agrega variables para probar la fórmula</p>
                        </div>

                        <div v-else class="test-inputs">
                          <div
                            v-for="tok in builderTokens"
                            :key="'tok-' + tok"
                            class="test-input-item"
                          >
                            <template v-if="getTokenCategory(tok) === 'date'">
                              <s-text :label="tok" v-model="tokenValues[tok]" type="date" dense filled />
                            </template>
                            <template v-else-if="getTokenCategory(tok) === 'number'">
                              <s-text :label="tok" v-model="tokenValues[tok]" type="number" :step="getNumberStep(tok)" dense filled />
                            </template>
                            <template v-else-if="getTokenCategory(tok) === 'bool'">
                              <v-select :label="tok" :items="boolItems" v-model="tokenValues[tok]" dense />
                            </template>
                            <template v-else>
                              <s-text :label="tok" v-model="tokenValues[tok]" dense filled />
                            </template>
                          </div>
                        </div>

                        <div class="test-actions">
                          <v-btn
                            block
                            color="success"
                            :disabled="builderTokens.length === 0"
                            @click="testMap(props.item)"
                          >
                            <v-icon left>mdi-play-circle-outline</v-icon>
                            Ejecutar Prueba
                          </v-btn>
                        </div>
                      </div>
                    </div>
                  </div>
                </v-col>
              </template>
            </v-row>
          </template>
        </s-crud>
        <!-- Diálogo para seleccionar valor de definición cuando se hace clic en columna TypeObject=0 -->
        <v-dialog v-model="defPicker.open" max-width="520">
          <v-card>
            <v-toolbar flat dense color="primary" dark>
              <v-toolbar-title style="font-size: 14px"
                >Seleccionar valor de definición</v-toolbar-title
              >
              <v-spacer></v-spacer>
              <v-btn icon @click="defPicker.open = false">
                <v-icon>mdi-close</v-icon>
              </v-btn>
            </v-toolbar>
            <v-divider></v-divider>
            <v-card-text>
              <div style="margin-bottom: 8px; font-size: 12px; color: #555">
                {{
                  (defPicker.tableObj &&
                    (defPicker.tableObj.text || defPicker.tableObj.name)) ||
                  ""
                }}
                /
                {{
                  (defPicker.colObj &&
                    (defPicker.colObj.text ||
                      (defPicker.colObj.raw &&
                        (defPicker.colObj.raw.AlsAlias ||
                          defPicker.colObj.raw.AlsNameObj)))) ||
                  ""
                }}
              </div>
              <s-select-definition
                :key="`defpicker_${defPicker.defID}`"
                :def="defPicker.defID"
                label="Elegir valor"
                :searchable="true"
                :returnObject="true"
                clearable
                autofocus
                @change="onPickDefinitionValue($event)"
                @input="onPickDefinitionValue($event)"
              />
            </v-card-text>
            <v-card-actions>
              <v-spacer></v-spacer>
              <v-btn text @click="defPicker.open = false">Cerrar</v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>
      </v-col>
    </v-row>
  </div>
</template>

<script>
import _sService from "@/services/Technicalassistance/Alert/AlertService.js";
import _aliasService from "@/services/General/Alias/AliasService.js";
import SSelectLot from "@/components/HarvestSchedule/SSelectLot.vue";
import TokenEditor from "@/components/Formula/TokenEditor.vue";
import SSelectDefinition from "@/components/Utils/SSelectDefinition.vue";
import _serviceDefinition from "@/services/General/DefinitionService.js";

export default {
  name: "AlertsG",
  components: { SSelectLot, TokenEditor, SSelectDefinition },
  data() {
    return {
      row: {} ,
      testVal: 0,
      tablesItems: [],
      columnsItems: [],
      selectedTables: [],
      columnsByTable: {},
      tokenValues: {},
      // Mapas alias<->interno y metadatos de tokens (para clases/tooltips)
      tokenMapAliasToInternal: {},
      tokenMapInternalToAlias: {},
      tokenMetaByAlias: {},
      definitionAliases: [],
      selectedDefinitionValues: {},
      selectedDefinitionAliasId: null,
      selectedDefinitionValue: null,
      operators: [
        { symbol: "+", value: "+" },
        { symbol: "-", value: "-" },
        { symbol: "×", value: "*" },
        { symbol: "÷", value: "/" },
        { symbol: "%", value: "%" },
        { symbol: "^", value: "^" },
        { symbol: "(", value: "(" },
        { symbol: ")", value: ")" },
        { symbol: ",", value: "," },
      ],
      logicalOperators: [
        { symbol: "AND", value: "AND" },
        { symbol: "OR", value: "OR" },
        { symbol: "NOT", value: "NOT" },
        { symbol: "=", value: "=" },
        { symbol: "<>", value: "<>" },
        { symbol: "<", value: "<" },
        { symbol: "<=", value: "<=" },
        { symbol: ">", value: ">" },
        { symbol: ">=", value: ">=" },
      ],
      functions: [
        // Reservado para funciones futuras (DATE, DATEDIFF, ADDDAYS, etc.)
      ],
      // Picker de definiciones al hacer clic en columna TypeObject=0
      defPicker: {
        open: false,
        defID: null,
        tableObj: null,
        colObj: null,
        item: null,
      },
      // items para bool en pruebas
      boolItems: [
        { text: "False (0)", value: "0" },
        { text: "True (1)", value: "1" },
      ],
    };
  },
  computed: {
    config() {
      return {
        model: {
          AldID: "ID",
          AldName: "string",
          AldRelatedTable: "string",
          AldRelatedField: "string",
          DedDescription: "string",
          AldValue: "string",
          FarmSubLot: "string",
          TypeCondition: "number",
          AldFormula: "string",
        },
        service: _sService,
        headers: [
          { text: "ID", value: "AldID", sortable: false },
          { text: "Nombre", value: "AldName", sortable: false },
          // { text: "Tabla", value: "AldRelatedTable", sortable: false },
          // { text: "Campo", value: "AldRelatedField", sortable: false },
          // { text: "Condición", value: "DedDescription", sortable: false },
          // { text: "Valor", value: "AldValue", sortable: false },
        ],
      };
    },
    // Tokens a badgear en el editor: variables + operadores lógicos (AND/OR/NOT) presentes
    badgeTokens() {
      // Ahora SOLO las variables (aliases); los operadores lógicos quedan como texto plano
      return this.builderTokens || [];
    },
    // Tokens variables (alias) usados por el editor y panel de pruebas.
    // Se filtran funciones y palabras clave lógicas.
    builderTokens() {
      const set = new Set();
      this.selectedTables.forEach((t) => {
        const tableAlias = t.text || t.name || t.value;
        (this.columnsByTable[t.name] || []).forEach((c) => {
          const colAlias =
            c &&
            (c.text ||
              c.value ||
              (c.raw && c.raw.AlsAlias) ||
              (c.raw && c.raw.AlsNameObj))
              ? c.text || c.value || c.raw.AlsAlias || c.raw.AlsNameObj
              : String(c);
          const token = this.aliasToToken(tableAlias, colAlias);
          set.add(token);
        });
      });
      (this.definitionAliases || []).forEach((d) => set.add(d.AlsAlias));
      // Extraer palabras de la fórmula escrita (para preservar variables tecleadas o pegadas)
      const formula = this.row && this.row.AldFormula ? String(this.row.AldFormula) : "";
      // No considerar palabras dentro de comillas al extraer candidatos de variables
      const re = /[A-Za-z_][A-Za-z0-9_]*/g;
      const masked = this.stripStrings(formula);
      const found = masked.match(re) || [];
      found.forEach((v) => set.add(v));
      // Filtrar funciones y palabras clave lógicas para que no aparezcan como variables
      const blacklist = new Set([
        "IF",
        "ABS",
        "ROUND",
        "MIN",
        "MAX",
        "SQRT",
        "RAIZ",
        "POW",
        "POWER",
        "EXP",
        "LN",
        "LOG",
        "LOG10",
        "CEIL",
        "FLOOR",
        "AND",
        "OR",
        "NOT",
      ]);
      let out = Array.from(set).filter((t) => !blacklist.has(String(t).toUpperCase()));
      // Ordenar por aparición en la fórmula (si existe), luego alfabético
      const f = String(this.row && this.row.AldFormula ? this.row.AldFormula : "");
      out.sort((a, b) => {
        const ia = f.indexOf(a);
        const ib = f.indexOf(b);
        if (ia === -1 && ib === -1) return a.localeCompare(b);
        if (ia === -1) return 1;
        if (ib === -1) return -1;
        if (ia !== ib) return ia - ib;
        return a.localeCompare(b);
      });
      return out;
    },
    selectedDef() {
      if (!this.selectedDefinitionAliasId) return null;
      return (
        this.definitionAliases.find((d) => d.AlsID === this.selectedDefinitionAliasId) ||
        null
      );
    },
    definitionAliasItems() {
      return (this.definitionAliases || []).map((d) => ({
        value: d.AlsID,
        text: d.AlsAlias,
      }));
    },
    // Clases para chips de tokens (azul para TypeObject=0, gris para otros)
    builderTokenClasses() {
      const out = {};
      Object.keys(this.tokenMetaByAlias).forEach((alias) => {
        const meta = this.tokenMetaByAlias[alias];
        if (meta && meta.className) out[alias] = meta.className;
      });
      return out;
    },
    // Tooltips de tokens mostrando alias e interno
    builderTokenTooltips() {
      const out = {};
      Object.keys(this.tokenMetaByAlias).forEach((alias) => {
        const meta = this.tokenMetaByAlias[alias];
        if (meta && meta.tooltip) out[alias] = meta.tooltip;
      });
      return out;
    },
  },
  methods: {
    // Clase visual para columnas del panel izquierdo (azul si es definición, gris si es columna normal)
    getColumnChipClass(tableObj, colObj) {
      const isDef = Number(colObj && colObj.raw && colObj.raw.TypeObject) === 0;
      return ["column-chip", isDef ? "tk-def" : "tk-col"];
    },
    // Tooltip de columna: alias -> interno
    getColumnChipTooltip(tableObj, colObj) {
      const tableAlias =
        tableObj && (tableObj.text || tableObj.name || tableObj.value)
          ? tableObj.text || tableObj.name || tableObj.value
          : String(tableObj);
      const colAlias =
        colObj &&
        (colObj.text ||
          colObj.value ||
          (colObj.raw && (colObj.raw.AlsAlias || colObj.raw.AlsNameObj)))
          ? colObj.text || colObj.value || colObj.raw.AlsAlias || colObj.raw.AlsNameObj
          : String(colObj);
      const tableName =
        tableObj && (tableObj.name || tableObj.value || tableObj.text)
          ? tableObj.name || tableObj.value || tableObj.text
          : String(tableObj);
      const colName =
        colObj && (colObj.value || (colObj.raw && colObj.raw.AlsNameObj))
          ? colObj.value || colObj.raw.AlsNameObj
          : String(colObj);
      return `${tableAlias}.${colAlias} -> ${tableName}.${colName}`;
    },
    async loadDefinitionAliases(defID) {
      try {
        if (!defID) return;
        const r = await _serviceDefinition.definitiongroup(
          defID,
          0,
          this.$fun.getUserID()
        );
        if (r.status !== 200) return;
        const rows = Array.isArray(r.data) ? r.data : [];
        // Guardar crudo sin filtrar (GenDefinitionDetailCE no trae TypeObject)
        this.definitionAliases = rows;
      } catch (e) {
        this.$fun.alert("Error cargando definiciones: " + e.message, "error");
      }
    },
    async rowSelected(rows) {
      this.row = rows && rows.length ? rows[0] : {};
      if (this.row && this.row.AldRelatedTable)
        this.loadColumns(this.row.AldRelatedTable);
      // Si viene fórmula interna (table.column), intentar mapear a alias para mostrar
      if (this.row && Number(this.row.TypeCondition) === 0 && this.row.AldFormula) {
        const hasDot = /[A-Za-z_][A-ZaEz0-9_]*\.[A-Za-z_][A-Za-z0-9_]*/.test(
          this.row.AldFormula
        );
        if (hasDot) {
          await this.ensureMappingsForInternalFormula(this.row.AldFormula);
          const display = this.toDisplayFormula(this.row.AldFormula);
          this.$set(this.row, "AldFormula", display);
        }
      }
    },
    async save(item) {
      if (!item.AldName) return this.$fun.alert("Ingrese nombre", "warning");
      if (item.TypeCondition === null || item.TypeCondition === undefined)
        return this.$fun.alert("Seleccione condición", "warning");
      if (item.TypeCondition !== 0) {
        if (!item.AldRelatedTable) return this.$fun.alert("Ingrese tabla", "warning");
        if (!item.AldRelatedField) return this.$fun.alert("Ingrese campo", "warning");
        if (!item.AldValue) return this.$fun.alert("Ingrese valor", "warning");
      } else {
        // if (!item.AldRelatedTable) return this.$fun.alert("Seleccione tabla relacionada", "warning");
        if (!item.AldFormula) return this.$fun.alert("Ingrese fórmula", "warning");
      }
      const doSave = async () => {
        await item.save();
        this.$refs.crud.refresh();
        this.rowSelected([item]);
      };
      if (Number(item.TypeCondition) === 0) {
        await this.saveInternalThenRestoreDisplay(item, doSave);
      } else {
        await doSave();
      }
    },
    insertIf(def, value, item) {
      const varName = def.AlsAlias;
      const val = value == null ? "" : value;
      // Inserta IF con valores por defecto 1 y 0
      const snippet = `${varName} = ${val}`;
      this.insertFormulaSnippet(snippet, item);
    },
    onSelectDefinition(def, selected, item) {
      const v =
        typeof selected === "object" && selected !== null ? selected.DedValue : selected;
      if (v !== null && v !== undefined && v !== "") {
        this.insertIf(def, v, item);
      }
    },
    onChangeSelectedDef(val) {
      // Al cambiar de definición seleccionada, limpiar el valor elegido
      this.selectedDefinitionValue = null;
    },
    async onChangeTable(value, item) {
      const table = value && value.value ? value.value : value;
      item.AldRelatedTable = table;
      item.AldRelatedField = null;
      this.columnsItems = [];
      if (table) this.loadColumns(table);

      // Si estamos en modo fórmula: limpiar fórmula y tokens, y setear fuentes de datos con la tabla seleccionada + TasVisit
      if (Number(item.TypeCondition) === 0) {
        // Limpiar fórmula y valores
        this.clearFormula(item);
        this.tokenValues = {};
        // Reiniciar fuentes
        this.selectedTables = [];
        this.columnsByTable = {};
        // Agregar la tabla principal
        const found = this.tablesItems.find((i) => i.value === table);
        if (found) {
          this.selectedTables.push({ name: found.value, text: found.text, raw: found.raw });
          await this.loadColumnsForBuilder(found.value);
        } else if (table) {
          this.selectedTables.push({ name: table, text: table, raw: null });
          await this.loadColumnsForBuilder(table);
        }
        // Agregar columnas de visita siempre
        await this.ensureVisitTableAdded();
      }
    },
    async loadTables() {
      const r = await _sService.tables("", this.$fun.getUserID());
      if (r.status === 200) {
        this.tablesItems = (r.data || []).map((x) => ({
          value: x.AlsNameObj,
          text: x.AlsAlias || x.AlsNameObj,
          raw: x,
        }));
      }
    },
    async loadColumns(tableName) {
      const column = await _sService.columns(tableName, this.$fun.getUserID());
      if (column.status === 200) {
        this.columnsItems = (column.data || []).map((x) => ({
          value: x.AlsNameObj,
          text: x.AlsAlias || x.AlsNameObj,
          raw: x,
        }));
      }
    },
    async addTable(value) {
      const name = value && value.value ? value.value : value;
      if (!name) return;
      const found = this.tablesItems.find((i) => i.value === name);
      const text = found ? found.text : String(name);
      if (!this.selectedTables.some((t) => t.name === name)) {
        // Guardar también el objeto completo para usos avanzados
        this.selectedTables.push({ name, text, raw: found ? found.raw : null });
        await this.loadColumnsForBuilder(name);
      }
    },
    async ensureVisitTableAdded() {
      const visitName = "TasVisit";
      const found = this.tablesItems.find((i) => i.value === visitName);
      const text = found ? found.text : visitName;
      const existsIdx = this.selectedTables.findIndex((t) => t.name === visitName);
      if (existsIdx === -1) {
        this.selectedTables.unshift({ name: visitName, text, raw: found ? found.raw : null });
      } else if (existsIdx > 0) {
        const el = this.selectedTables.splice(existsIdx, 1)[0];
        this.selectedTables.unshift(el);
      }
      await this.loadColumnsForBuilder(visitName);
    },
    async loadColumnsForBuilder(tableName) {
      const r = await _sService.columns(tableName, this.$fun.getUserID());
      if (r.status === 200) {
        const cols = (r.data || []).map((x) => ({
          value: x.AlsNameObj,
          text: x.AlsAlias || x.AlsNameObj,
          raw: x,
        }));
        this.$set(this.columnsByTable, tableName, cols);
        // Construir/actualizar mapas de tokens para esta tabla
        const st = this.selectedTables.find((t) => t.name === tableName);
        const tableAlias = st ? st.text || st.name || st.value : tableName;
        cols.forEach((c) => {
          const colAlias =
            c.text ||
            c.value ||
            (c.raw && (c.raw.AlsAlias || c.raw.AlsNameObj)) ||
            String(c.value);
          const aliasToken = this.aliasToToken(tableAlias, colAlias);
          const internalToken = `${tableName}.${c.value}`;
          this.$set(this.tokenMapAliasToInternal, aliasToken, internalToken);
          this.$set(this.tokenMapInternalToAlias, internalToken, aliasToken);
          const isDef = Number(c.raw && c.raw.TypeObject) === 0;
          const tooltip = `${tableAlias}.${colAlias} -> ${internalToken}`;
          // Mapear tipo SQL a categoría para validación
          const sqlType = (c.raw && c.raw.DataType) ? String(c.raw.DataType).toLowerCase() : null;
          const typeCat = this.sqlTypeToCategory(sqlType);
          this.$set(this.tokenMetaByAlias, aliasToken, {
            className: isDef ? "tk-def" : "tk-col",
            tooltip,
            internal: internalToken,
            aliasDisplay: `${tableAlias}.${colAlias}`,
            dataType: sqlType,
            typeCat: typeCat,
          });
        });
      }
    },
    removeTable(t) {
      const name = t && t.name ? t.name : t;
      if (String(name) === "TasVisit") return;
      this.selectedTables = this.selectedTables.filter((x) => x.name !== name);
      this.$delete(this.columnsByTable, name);
    },
    aliasToToken(tableAlias, columnAlias) {
      const raw = `${tableAlias}_${columnAlias}`;
      let tk = raw.normalize("NFD").replace(/[\u0300-\u036f]/g, "");
      tk = tk.replace(/[^A-Za-z0-9_]/g, "_");
      if (/^[0-9]/.test(tk)) tk = "_" + tk;
      return tk;
    },
    insertTokenFrom(tableObj, colObj, item) {
      // Si la columna es una definición (TypeObject=0) con DefID, abrir selector de definición
      if (
        colObj &&
        colObj.raw &&
        Number(colObj.raw.TypeObject) === 0 &&
        colObj.raw.DefID
      ) {
        // IMPORTANTE: Limpiar estado previo completamente antes de abrir el modal
        this.defPicker.open = false;
        this.defPicker.defID = null;
        this.defPicker.tableObj = null;
        this.defPicker.colObj = null;
        this.defPicker.item = null;
        // Esperar un tick para que Vue destruya y recree el componente s-select-definition
        this.$nextTick(() => {
          // Ahora asignar el nuevo estado
          this.defPicker.defID = colObj.raw.DefID;
          this.defPicker.tableObj = tableObj;
          this.defPicker.colObj = colObj;
          this.defPicker.item = item;
          this.defPicker.open = true;
        });
        return; // no insertar directo el token, esperar selección del valor
      }
      const tableAlias =
        tableObj && (tableObj.text || tableObj.name || tableObj.value)
          ? tableObj.text || tableObj.name || tableObj.value
          : String(tableObj);
      const colAlias =
        colObj &&
        (colObj.text ||
          colObj.value ||
          (colObj.raw && (colObj.raw.AlsAlias || colObj.raw.AlsNameObj)))
          ? colObj.text || colObj.value || colObj.raw.AlsAlias || colObj.raw.AlsNameObj
          : String(colObj);
      const token = this.aliasToToken(tableAlias, colAlias);
      // Inserción inteligente tipo calculadora: variable reemplaza a la última variable si corresponde
      this.insertVariableToken(token, item);
      if (this.tokenValues[token] === undefined) this.$set(this.tokenValues, token, 0);
    },
    onPickDefinitionValue(val) {
      // val puede ser objeto o valor primitivo segun returnObject
      const v = val && typeof val === "object" ? val.DedValue : val;
      // Ignorar cambios vacíos; mantener el diálogo abierto
      if (v === null || v === undefined || v === "") return;
      const { tableObj, colObj, item } = this.defPicker;
      const tableAlias =
        tableObj && (tableObj.text || tableObj.name || tableObj.value)
          ? tableObj.text || tableObj.name || tableObj.value
          : String(tableObj);
      const colAlias =
        colObj &&
        (colObj.text ||
          colObj.value ||
          (colObj.raw && (colObj.raw.AlsAlias || colObj.raw.AlsNameObj)))
          ? colObj.text || colObj.value || colObj.raw.AlsAlias || colObj.raw.AlsNameObj
          : String(colObj);
      const token = this.aliasToToken(tableAlias, colAlias);
      const snippet = `${token} = ${v}`;
      this.appendToFormula(snippet, item);
      if (this.tokenValues[token] === undefined) this.$set(this.tokenValues, token, 0);
      this.defPicker.open = false;
    },
    insertOperator(op, item) {
      // Inserción inteligente de operadores: si el último token es operador, lo reemplaza
      this.insertOperatorSmart(op, item);
    },
    insertFormulaSnippet(tpl, item) {
      this.appendToFormula(tpl, item);
    },
    appendToFormula(text, item) {
      const editorRef = this.$refs[`tokenEditor_${item.AldID}`];
      // token-editor API: insertTextAtCursor
      if (editorRef && typeof editorRef.insertTextAtCursor === "function") {
        editorRef.insertTextAtCursor(text);
      } else {
        // fallback: concatenar al final (mantener reactividad)
        const cur = item && item.AldFormula ? String(item.AldFormula) : "";
        const next = (cur ? cur + " " : "") + text;
        if (item) this.$set(item, "AldFormula", next);
      }
    },
    updateFormula(value, item) {
      item.AldFormula = value;
    },
    clearFormula(item) {
      if (item) this.$set(item, "AldFormula", "");
      this.$forceUpdate();
    },
    // Validación: usa conversión a interno y saneo para backend + validación de tipos
    validate(item) {
      if (!item.AldFormula) return this.$fun.alert("Ingrese fórmula", "warning");
      if (!this.safeFormula(item.AldFormula)) return;
      const displayF = String(item.AldFormula || "");
      const internalF = this.toInternalFormula(displayF);
      const backendF = this.toBackendFormula(internalF);
      _sService.validateFormula(backendF, this.$fun.getUserID()).then((r) => {
        if (!(r.status === 200 && r.data && r.data.isValid)) {
          return this.$fun.alert("Fórmula inválida", "warning");
        }
        this.$fun.alert(`Fórmula válida`, "success");
      });
    },
    testMap(item) {
      if (!item.AldFormula) return this.$fun.alert("Ingrese fórmula", "warning");
      if (!this.safeFormula(item.AldFormula)) return;
      const displayF = String(item.AldFormula || "");
      const internalF = this.toInternalFormula(displayF);
      const used = {};
      const maskedDisplay = this.stripStrings(displayF);
      const maskedInternal = this.stripStrings(internalF);
      Object.keys(this.tokenValues).forEach((alias) => {
        const internalKey = this.tokenMapAliasToInternal[alias] || alias;
        const reAlias = new RegExp(`\\b${this.escapeRegExp(alias)}\\b`, "g");
        const reInternal = new RegExp(`\\b${this.escapeRegExp(internalKey)}\\b`, "g");
        if (reAlias.test(maskedDisplay) || reInternal.test(maskedInternal)) {
          const raw = this.tokenValues[alias];
          const cat = this.getTokenCategory(alias);
          let val = raw;
          if (cat === 'number') {
            // mantener numérico; backend puede parsear decimal como string también
            if (raw !== null && raw !== undefined && String(raw).trim() !== '' && !isNaN(Number(raw))) {
              val = String(raw);
            } else {
              val = '0';
            }
          } else if (cat === 'bool') {
            // enviar '1' o '0'
            val = raw === '1' || raw === 1 || raw === true ? '1' : '0';
          } else if (cat === 'date') {
            // enviar como string; v-text-field date retorna yyyy-MM-dd
            val = raw ? String(raw) : '';
          } else {
            val = raw == null ? '' : String(raw);
          }
          used[internalKey] = val;
        }
      });
      const backendF = this.toBackendFormula(internalF);
      const usedBackend = this.toBackendUsedMap(used);
      _sService.testFormulaMap(backendF, usedBackend, this.$fun.getUserID()).then((r) => {
        if (r.status === 200) this.$fun.alert(`Resultado: ${r.data.result}`, "success");
      });
    },
    safeFormula(formula) {
      // 1) Tokens permitidos (funciones) + palabras clave lógicas
      const allowedFns = new Set([
        "IF",
        "ABS",
        "ROUND",
        "MIN",
        "MAX",
        "SQRT",
        "RAIZ",
        "POW",
        "POWER",
        "EXP",
        "LN",
        "LOG",
        "LOG10",
        "CEIL",
        "FLOOR",
        // futuras funciones de fecha:
        // "DATE", "DATEDIFF", "ADDDAYS",
      ]);
      const allowedKeywords = new Set(["AND", "OR", "NOT"]);

      // Ignorar literales en comillas para no marcar palabras como variables
      const masked = this.stripStrings(String(formula));
      const tokens = masked.match(/[A-Za-z_][A-Za-z0-9_]*/g) || [];
      const allowedVars = new Set(this.builderTokens.concat(["VAL"]));

      for (const t of tokens) {
        const up = String(t).toUpperCase();
        if (allowedKeywords.has(up)) continue; // permitir AND/OR/NOT
        if (allowedFns.has(up)) continue;
        if (allowedVars.has(t)) continue;
        this.$fun.alert(`Token no permitido: ${t}`, "warning");
        return false;
      }

      // 2) Validación de tipos básica entre operandos y operadores
      const typeErrors = this.validateTypeRules(formula);
      if (typeErrors) {
        this.$fun.alert(typeErrors, "warning");
        return false;
      }

      // 3) Caracteres válidos
      if (/[^0-9A-Za-z_\s\+\-\*\/\^%\(\),\.<>=\'\"\/\-]/.test(formula)) {
        this.$fun.alert("Caracteres no permitidos en la fórmula", "warning");
        return false;
      }
      return true;
    },
    // ---- Conversión alias<->interno ----
    escapeRegExp(s) {
      return String(s).replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    },
    toInternalFormula(displayFormula) {
      let out = String(displayFormula || "");
      const aliases = Object.keys(this.tokenMapAliasToInternal).sort(
        (a, b) => b.length - a.length
      );
      aliases.forEach((alias) => {
        const internal = this.tokenMapAliasToInternal[alias];
        const re = new RegExp(`\\b${this.escapeRegExp(alias)}\\b`, "g");
        out = out.replace(re, internal);
      });
      return out;
    },
    // Reemplaza cada token interno table.column por table_column (solo variables, no números decimales)
    toBackendFormula(internalFormula) {
      let out = String(internalFormula || "");
      const internals = Object.keys(this.tokenMapInternalToAlias).sort(
        (a, b) => b.length - a.length
      );
      internals.forEach((intr) => {
        const [tbl, col] = String(intr).split(".");

        if (!tbl || !col) return;
        const re = new RegExp(
          `\\b${this.escapeRegExp(tbl)}\\.${this.escapeRegExp(col)}\\b`,
          "g"
        );
        out = out.replace(re, `${tbl}_${col}`);
      });
      out = this.normalizeLogicalOps(out);
      return out;
    },
    normalizeLogicalOps(expr) {
      const masked = this.stripStrings(String(expr || ""));
      const indexes = [];
      const re = /\b(AND|OR|NOT)\b/g;
      let m;
      while ((m = re.exec(masked)) !== null) {
        indexes.push({ idx: m.index, len: m[0].length });
      }
      let out = String(expr || "");
      for (let i = indexes.length - 1; i >= 0; i--) {
        const { idx, len } = indexes[i];
        const seg = out.substr(idx, len);
        out = out.slice(0, idx) + seg.toLowerCase() + out.slice(idx + len);
      }
      return out;
    },
    toBackendUsedMap(usedInternal) {
      // out debe ser un objeto, pero sort solo aplica a arrays
      const outObj = {};
      Object.keys(usedInternal || {}).forEach((k) => {
        if (k.includes(".")) outObj[k.replace(/\./g, "_")] = usedInternal[k];
        else outObj[k] = usedInternal[k];
      });
      // Si necesitas orden, crea un array ordenado de claves y reconstruye el objeto
      const f = String(this.row && this.row.AldFormula ? this.row.AldFormula : "");
      const keys = Object.keys(outObj);
      keys.sort((a, b) => {
        const visitWeight = (aliasToken) => {
          const meta = this.tokenMetaByAlias[aliasToken];
          const intr = meta && meta.internal ? String(meta.internal) : "";
          return intr.startsWith("TasVisit.") ? 0 : 1;
        };
        const wa = visitWeight(a);
        const wb = visitWeight(b);
        if (wa !== wb) return wa - wb;
        const ia = f.indexOf(a);
        const ib = f.indexOf(b);
        if (ia === -1 && ib === -1) return a.localeCompare(b);
        if (ia === -1) return 1;
        if (ib === -1) return -1;
        if (ia !== ib) return ia - ib;
        return a.localeCompare(b);
      });
      // Reconstruir el objeto ordenado
      const sortedOut = {};
      keys.forEach((k) => {
        sortedOut[k] = outObj[k];
      });
      return sortedOut;
    },
    toDisplayFormula(internalFormula) {
      let out = String(internalFormula || "");
      const internals = Object.keys(this.tokenMapInternalToAlias).sort(
        (a, b) => b.length - a.length
      );
      internals.forEach((intr) => {
        const alias = this.tokenMapInternalToAlias[intr];
        const [tbl, col] = intr.split(".");

        const re = new RegExp(
          `\\b${this.escapeRegExp(tbl)}\\.${this.escapeRegExp(col)}\\b`,
          "g"
        );
        out = out.replace(re, alias);
      });
      return out;
    },
    async ensureMappingsForInternalFormula(intFormula) {
      const s = String(intFormula || "");
      const m = s.match(/[A-Za-z_][A-Za-z0-9_]*\.[A-Za-z_][A-Za-z0-9_]*/g) || [];
      const tables = Array.from(new Set(m.map((x) => x.split(".")[0])));
      for (const t of tables) {
        if (!this.selectedTables.some((x) => x.name === t)) {
          const found = this.tablesItems.find((i) => i.value === t);
          const text = found ? found.text || found.value || t : t;
          this.selectedTables.push({ name: t, text, raw: found ? found.raw : null });
        }
        await this.loadColumnsForBuilder(t);
      }
    },
    async saveInternalThenRestoreDisplay(item, saveFn) {
      const originalDisplay = item.AldFormula;
      const internal = this.toInternalFormula(originalDisplay);
      item.AldFormula = internal;
      try {
        await saveFn();
      } finally {
        // Restaurar al usuario la versión con alias
        item.AldFormula = originalDisplay;
      }
    },

    // ---- Inserción inteligente tipo calculadora ----
    insertVariableToken(token, item) {
      const cur = String(item && item.AldFormula ? item.AldFormula : "");
      const toks = this.lexFormula(cur);
      const last = this.getLastToken(toks);
      if (last && last.type === "var") {
        // Reemplaza la última variable
        const next = cur.slice(0, last.start) + token + cur.slice(last.end);
        const nextToks = this.lexFormula(next);
        if (!this.isSequenceValid(nextToks)) {
          this.$fun.alert("Secuencia inválida de tokens", "warning");
          return;
        }
        this.$set(item, "AldFormula", next);
        this.$nextTick(() => this.$forceUpdate());
        return;
      }
      // Por defecto, insertar con espacio si corresponde
      const needSpace = cur && !/[\s\(]$/.test(cur);
      const next = cur + (needSpace ? " " : "") + token;
      const nextToks = this.lexFormula(next);
      if (!this.isSequenceValid(nextToks)) {
        this.$fun.alert("Secuencia inválida de tokens", "warning");
        return;
      }
      this.$set(item, "AldFormula", next);
      this.$nextTick(() => this.$forceUpdate());
    },

    insertOperatorSmart(op, item) {
      const cur = String(item && item.AldFormula ? item.AldFormula : "");
      const toks = this.lexFormula(cur);
      const last = this.getLastToken(toks);
      // No reemplazar paréntesis
      if (last && last.type === "op") {
        // Regla: si ambos son comparadores, reemplazar; si ambos son aritméticos, reemplazar; si mezcla que no tiene sentido simplemente sustituir.
        const next = cur.slice(0, last.start) + op + cur.slice(last.end);
        const nextToks = this.lexFormula(next);
        if (!this.isSequenceValid(nextToks)) {
          this.$fun.alert("Secuencia inválida de tokens", "warning");
          return;
        }
        this.$set(item, "AldFormula", next);
        this.$nextTick(() => this.$forceUpdate());
        return;
      }
      const needSpace = cur && !/[\s\(]$/.test(cur);
      const next = cur + (needSpace ? " " : "") + op + " ";
      const nextToks = this.lexFormula(next);
      if (!this.isSequenceValid(nextToks)) {
        this.$fun.alert("Secuencia inválida de tokens", "warning");
        return;
      }
      this.$set(item, "AldFormula", next);
      this.$nextTick(() => this.$forceUpdate());
    },
    // ---- Tokenizador simple para última pieza ----
    lexFormula(str) {
      const s = String(str || "");
      const out = [];
      let i = 0;
      const push = (type, value, start, end) => out.push({ type, value, start, end });
      const isLetter = (ch) => /[A-Za-z_]/.test(ch);
      const isDigit = (ch) => /[0-9]/.test(ch);
      const ops2 = new Set(["<=", ">=", "<>"]);
      const ops1 = new Set(["+", "-", "*", "/", "^", "=", "<", ">", ","]);
      while (i < s.length) {
        const ch = s[i];
        if (ch === '"' || ch === "'") {
          const q = ch;
          let j = i + 1;
          let esc = false;
          while (j < s.length) {
            const cj = s[j];
            if (!esc && cj === q) {
              j++;
              break;
            }
            esc = !esc && cj === "\\";
            j++;
          }
          push("string", s.slice(i, j), i, j);
          i = j;
          continue;
        }
        if (isLetter(ch)) {
          let j = i + 1;
          while (j < s.length && /[A-Za-z0-9_]/.test(s[j])) j++;
          const w = s.slice(i, j);
          // Clasificar como variable si pertenece a builderTokens o es palabra clave lógica
          const up = w.toUpperCase();
          const isLogical = up === "AND" || up === "OR" || up === "NOT";
          const isVar = (this.builderTokens || []).includes(w);
          push(isLogical ? "op" : isVar ? "var" : "ident", w, i, j);
          i = j;
          continue;
        }
        if (isDigit(ch)) {
          let j = i + 1;
          while (j < s.length && /[0-9\.]/.test(s[j])) j++;
          push("number", s.slice(i, j), i, j);
          i = j;
          continue;
        }
        if (i + 1 < s.length) {
          const two = s.slice(i, i + 2);
          if (ops2.has(two)) {
            push("op", two, i, i + 2);
            i += 2;
            continue;
          }
        }
        if (ops1.has(ch)) {
          push("op", ch, i, i + 1);
          i++;
          continue;
        }
        if (ch === "(" || ch === ")") {
          push("paren", ch, i, i + 1);
          i++;
          continue;
        }
        if (/\s/.test(ch)) {
          let j = i + 1;
          while (j < s.length && /\s/.test(s[j])) j++;
          push("ws", s.slice(i, j), i, j);
          i = j;
          continue;
        }
        // Cualquier otro char
        push("unk", ch, i, i + 1);
        i++;
      }
      return out;
    },
    // Validar secuencias calculadora (evitar var var, op op, etc.)
    isSequenceValid(tokens) {
      let prev = null;
      for (const t of tokens) {
        if (t.type === "ws" || t.type === "unk") continue;
        if (prev) {
          // var seguida de var
          if (prev.type === "var" && t.type === "var") return false;
          // operador seguido de operador (excepto paréntesis que se maneja aparte)
          if (prev.type === "op" && t.type === "op") return false;
        }
        prev = t;
      }
      return true;
    },
    getLastToken(tokens) {
      for (let k = tokens.length - 1; k >= 0; k--) {
        const t = tokens[k];
        if (t.type === "ws") continue;
        return t;
      }
      return null;
    },
    // ---- Verificación de forma booleana ----
    stripStrings(s) {
      return String(s || "").replace(/(["'])((?:\\.|(?!\1).)*)\1/g, (m) =>
        '"'.padEnd(m.length, '"')
      );
    },
    looksBoolean(formula) {
      const f = this.stripStrings(String(formula || ""));
      // IF con 3 argumentos a nivel superficial
      const idx = f.toUpperCase().indexOf("IF(");
      if (idx >= 0) {
        let depth = 0,
          i = idx;
        let commas = 0;
        for (; i < f.length; i++) {
          const ch = f[i];
          if (ch === "(") depth++;
          else if (ch === ")") {
            depth--;
            if (depth === 0) break;
          } else if (ch === "," && depth === 1) commas++;
        }
        if (commas >= 2) return true;
      }
      // O presencia de comparadores fuera de comillas
      if (/(<=|>=|<>|=|<|>)/.test(f)) return true;
      return false;
    },

    // ====== VALIDACIÓN DE TIPOS ======
    sqlTypeToCategory(sqlType) {
      const t = (sqlType || "").toLowerCase();
      if (["int", "tinyint", "smallint", "bigint", "decimal", "numeric", "float", "real", "money", "smallmoney"].includes(t)) return "number";
      if (["bit"].includes(t)) return "bool";
      if (["date", "datetime", "smalldatetime", "datetime2", "time"].includes(t)) return "date";
      if (["uniqueidentifier"].includes(t)) return "string";
      return "string"; // por defecto
    },
    getTokenCategory(aliasToken) {
      const meta = this.tokenMetaByAlias[aliasToken];
      return meta && meta.typeCat ? meta.typeCat : null;
    },
    getNumberStep(aliasToken) {
      const meta = this.tokenMetaByAlias[aliasToken];
      const dt = meta && meta.dataType ? meta.dataType.toLowerCase() : '';
      // enteros: step 1, si decimal: step any
      if (["int","tinyint","smallint","bigint"].includes(dt)) return 1;
      return "any";
    },
    isDateLiteralToken(tok) {
      // token es string con comillas: '2025-11-14' o '14/11/2025' o '14-11-2025'
      if (!tok || !tok.value) return false;
      if (tok.type !== "string") return false;
      const s = tok.value.slice(1, -1); // quitar comillas
      // yyyy-mm-dd
      const re1 = /^\d{4}-\d{2}-\d{2}$/;
      // dd/mm/yyyy
      const re2 = /^\d{2}\/\d{2}\/\d{4}$/;
      // dd-mm-yyyy
      const re3 = /^\d{2}-\d{2}-\d{4}$/;
      return re1.test(s) || re2.test(s) || re3.test(s);
    },
    validateTypeRules(formula) {
      const toks = this.lexFormula(formula);
      // Buscar operaciones binarias alrededor de operadores
      const isArith = (op) => ["+", "-", "*", "/", "^", "%"].includes(op);
      const isComp = (op) => ["=", "<>", "<", ">", "<=", ">="].includes(op);
      // Helper para obtener el token operando más cercano a la izquierda/derecha
      const prevOperand = (idx) => {
        for (let i = idx - 1; i >= 0; i--) {
          const t = toks[i];
          if (!t) break;
          if (t.type === "ws" || t.type === "paren" || t.type === "ident") continue;
          if (t.type === "var" || t.type === "number" || t.type === "string") return t;
          // cortar si encontramos otro operador
          if (t.type === "op") break;
        }
        return null;
      };
      const nextOperand = (idx) => {
        for (let i = idx + 1; i < toks.length; i++) {
          const t = toks[i];
          if (!t) break;
          if (t.type === "ws" || t.type === "paren" || t.type === "ident") continue;
          if (t.type === "var" || t.type === "number" || t.type === "string") return t;
          if (t.type === "op") break;
        }
        return null;
      };
      const typeOfTok = (t) => {
        if (!t) return null;
        if (t.type === "number") return "number";
        if (t.type === "string") {
          // detectar literal de fecha
          return this.isDateLiteralToken(t) ? "date" : "string";
        }
        if (t.type === "var") {
          const alias = t.value;
          return this.getTokenCategory(alias) || null;
        }
        return null;
      };

      for (let i = 0; i < toks.length; i++) {
        const t = toks[i];
        if (t.type !== "op") continue;
        const op = t.value.toUpperCase();
        if (["AND", "OR", "NOT", ",", "(", ")"].includes(op)) continue;
        const left = prevOperand(i);
        const right = nextOperand(i);
        const lt = typeOfTok(left);
        const rt = typeOfTok(right);
        if (!left || !right) continue; // se valida por NCalc/SQL luego
        if (isArith(op)) {
          // Solo números a ambos lados
          if (lt !== "number" || rt !== "number") {
            return `Operación aritmética inválida: se requieren números a ambos lados de '${t.value}'`;
          }
        }
        if (isComp(op)) {
          // Permitir number-number, date-date, string-string, bool-bool
          const ok =
            (lt && rt) && (
              (lt === "number" && rt === "number") ||
              (lt === "date" && rt === "date") ||
              (lt === "string" && rt === "string") ||
              (lt === "bool" && rt === "bool")
            );
          if (!ok) {
            return `Comparación inválida: tipos incompatibles (${lt || 'desconocido'} ${t.value} ${rt || 'desconocido'})`;
          }
        }
      }
      return null; // sin errores
    },
  },
  watch: {
    selectedTables: {
      handler(n) {},
      deep: true,
    },
    definitionAliases: {
      handler(n) {},
      deep: true,
    },
    "row.AldFormula": function (n) {},
  },
  async mounted() {
    await this.loadTables();
    // Asegurar siempre TasVisit como fuente principal al iniciar
    await this.ensureVisitTableAdded();
  },
};
</script>
<style scoped>
.formula-container {
  display: grid;
  grid-template-columns: 300px 1fr 320px;
  gap: 20px;
  min-height: 600px;
  background: #fafbfc;
  border-radius: 12px;
  padding: 20px;
  border: 1px solid #e1e4e8;
}

/* === PANELES === */
.tables-panel,
.formula-panel,
.test-panel {
  background: white;
  border-radius: 10px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.panel-header {
  padding: 16px 20px;
  border-bottom: 1px solid #e1e4e8;
  background: linear-gradient(to bottom, #ffffff, #f6f8fa);
}

.panel-title {
  font-size: 13px;
  font-weight: 600;
  color: #24292e;
  margin: 0;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

/* === PANEL DE TABLAS === */
.table-selector {
  padding: 16px;
  border-bottom: 1px solid #e1e4e8;
}

.tables-list {
  flex: 1;
  overflow-y: auto;
  padding: 12px;
}

.table-item {
  margin-bottom: 16px;
  padding: 12px;
  background: #f6f8fa;
  border-radius: 8px;
  border: 1px solid #e1e4e8;
  transition: all 0.2s ease;
}

.table-item:hover {
  border-color: #1976d2;
  box-shadow: 0 2px 8px rgba(25, 118, 210, 0.1);
}

.table-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 10px;
}

.table-name {
  font-weight: 600;
  font-size: 13px;
  color: #24292e;
}

.remove-btn {
  opacity: 0.6;
  transition: opacity 0.2s;
}

.remove-btn:hover {
  opacity: 1;
}

.columns-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.column-chip {
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: 11px !important;
  height: 26px !important;
}

.column-chip.tk-def {
  background: linear-gradient(135deg, #1976d2 0%, #1565c0 100%) !important;
  color: #ffffff !important;
  border-color: #1565c0 !important;
}

.column-chip.tk-col {
  background: #eceff1 !important;
  color: #263238 !important;
  border-color: #cfd8dc !important;
}

.column-chip:hover {
  transform: translateY(-1px);
  box-shadow: 0 2px 6px rgba(25, 118, 210, 0.15);
}

/* === PANEL DE FÓRMULA === */
.formula-toolbar {
  padding: 16px;
  background: #f6f8fa;
  border-bottom: 1px solid #e1e4e8;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.toolbar-section {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.toolbar-label {
  font-size: 11px;
  font-weight: 600;
  color: #586069;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin: 0;
}

.button-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(60px, 1fr));
  gap: 6px;
}

.operator-btn,
.function-btn {
  height: 34px !important;
  font-weight: 600;
  font-size: 13px !important;
  transition: all 0.2s ease;
}

.operator-btn:hover {
  background: #e1e4e8 !important;
  transform: translateY(-1px);
}

.function-btn {
  padding: 0 8px !important;
}

.function-btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 2px 6px rgba(25, 118, 210, 0.2);
}

.editor-wrapper {
  flex: 1;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  overflow: hidden;
}

.editor-hint {
  display: flex;
  align-items: flex-start;
  gap: 6px;
  padding: 10px 12px;
  background: #e3f2fd;
  border-radius: 6px;
  font-size: 12px;
  color: #0d47a1;
  line-height: 1.5;
}

.action-buttons {
  display: flex;
  padding: 16px;
  border-top: 1px solid #e1e4e8;
  background: #f6f8fa;
}

/* === PANEL DE PRUEBAS === */
.test-content {
  flex: 1;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  overflow-y: auto;
}

.test-inputs {
  display: flex;
  flex-direction: column;
  gap: 12px;
  flex: 1;
}

.test-input-item {
  padding: 8px;
  background: #f6f8fa;
  border-radius: 6px;
}

.test-actions {
  padding-top: 12px;
  border-top: 1px solid #e1e4e8;
  margin-top: auto;
}

/* === ESTADO VACÍO === */
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
  text-align: center;
  color: #586069;
  gap: 12px;
}

.empty-state p {
  margin: 0;
  font-size: 13px;
}

/* === RESPONSIVE === */
@media (max-width: 1400px) {
  .formula-container {
    grid-template-columns: 1fr;
    grid-template-rows: auto auto auto;
  }
}

@media (max-width: 960px) {
  .formula-container {
    padding: 12px;
    gap: 12px;
  }

  .panel-header {
    padding: 12px 16px;
  }

  .formula-toolbar {
    padding: 12px;
  }

  .button-grid {
    grid-template-columns: repeat(auto-fill, minmax(50px, 1fr));
  }
}
</style>
